import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BoardContactService } from '../../../../services/board-contact.service';
import { NotificationService } from '../../../../services/notifications.service';
import { BoardContact, NewCardContact } from '../../board-v3.types';
import {
  customFieldEmailValidator,
  customFieldPhoneValidator,
  normalizePhonePayloadValue,
} from '../../custom-field.validators';
import { getContactErrorKey, getContactName, getDuplicateContact } from '../contact.utils';
import { AddressInput } from '../../../../interfaces/card';

export interface ContactFormResult {
  contact: BoardContact;
}

@Component({
  selector: 'app-board-v3-contact-form',
  templateUrl: './contact-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class BoardV3ContactFormComponent implements OnInit {
  /** Cards the new contact is linked to right away */
  @Input() cardIds: string[] = [];
  @Output() close = new EventEmitter<ContactFormResult | void>();

  private readonly contactService = inject(BoardContactService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = new FormGroup({
    first_name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    last_name: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
    middle_name: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
    phones: new FormArray<FormControl<string>>([]),
    emails: new FormArray<FormControl<string>>([]),
    addresses: new FormControl<AddressInput[]>([], { nonNullable: true }),
  });

  protected saving = false;
  protected duplicate: BoardContact | null = null;
  protected readonly getContactName = getContactName;

  get phones(): FormArray<FormControl<string>> {
    return this.form.controls.phones;
  }

  get emails(): FormArray<FormControl<string>> {
    return this.form.controls.emails;
  }

  ngOnInit(): void {
    this.addPhone();
    this.addEmail();
  }

  protected addPhone(value = ''): void {
    this.phones.push(new FormControl(value, { nonNullable: true, validators: [customFieldPhoneValidator] }));
  }

  protected addEmail(value = ''): void {
    this.emails.push(new FormControl(value, { nonNullable: true, validators: [customFieldEmailValidator] }));
  }

  protected removePhone(index: number): void {
    this.phones.removeAt(index);
    if (!this.phones.length) this.addPhone();
  }

  protected removeEmail(index: number): void {
    this.emails.removeAt(index);
    if (!this.emails.length) this.addEmail();
  }

  protected cancel(): void {
    this.close.emit();
  }

  protected submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving) return;

    this.duplicate = null;
    this.saving = true;
    this.contactService
      .createContact({
        ...this.buildPayload(),
        ...(this.cardIds.length ? { card_ids: this.cardIds } : {}),
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (contact) => {
          this.notificationService.showSuccess('', this.translate('board_v3_contact_saved'));
          this.close.emit({ contact });
        },
        error: (error) => {
          this.duplicate = getDuplicateContact(error);
          if (this.duplicate) return;

          this.notificationService.showError('', this.translate(getContactErrorKey(error)));
        },
      });
  }

  /** Links the contact that already owns the phone or email instead of creating a copy */
  protected linkDuplicate(): void {
    const duplicate = this.duplicate;
    if (!duplicate || !this.cardIds.length || this.saving) return;

    this.saving = true;
    this.contactService
      .linkCards(duplicate.id, this.cardIds)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (contact) => this.close.emit({ contact }),
        error: (error) => this.notificationService.showError('', this.translate(getContactErrorKey(error))),
      });
  }

  private buildPayload(): NewCardContact {
    const raw = this.form.getRawValue();
    const phones = [...new Set(raw.phones.map((phone) => normalizePhonePayloadValue(phone)).filter(Boolean))];
    const emails = [...new Set(raw.emails.map((email) => email.trim().toLowerCase()).filter(Boolean))];

    return {
      first_name: raw.first_name.trim(),
      last_name: raw.last_name.trim() || undefined,
      middle_name: raw.middle_name.trim() || undefined,
      phones,
      emails,
      addresses: raw.addresses,
    };
  }

  private translate(key: string): string {
    return String(this.translateService.instant(key));
  }
}
